(() => {
  'use strict';

  if (window.__snogoCartGuardV3) {
    return;
  }

  window.__snogoCartGuardV3 = true;

  const ROOT = window.Shopify?.routes?.root || '/';

  const SELECTORS = {
    checkout:
      '[data-cart-guard-checkout], #checkout, button[name="checkout"]',

    quantityInput:
      'input[name="updates[]"], input.quantity__input',

    quantityControl:
      '.quantity__button, button[name="plus"], button[name="minus"], cart-remove-button, cart-remove-button a, cart-remove-button button',

    message: '#straw-threshold-message',
    data: '#cart-guard-data',
    footer: '#main-cart-footer'
  };

  let lastCartAction = 0;
  let validationTimer = null;
  let validationPromise = null;

  function sleep(milliseconds) {
    return new Promise((resolve) => {
      window.setTimeout(resolve, milliseconds);
    });
  }

  function getCheckoutButtons() {
    return document.querySelectorAll(SELECTORS.checkout);
  }

  function getMessageElement() {
    return document.querySelector(SELECTORS.message);
  }

  function setCheckoutAllowed(allowed) {
    getCheckoutButtons().forEach((button) => {
      button.disabled = !allowed;
      button.hidden = !allowed;
      button.setAttribute(
        'aria-disabled',
        allowed ? 'false' : 'true'
      );
    });
  }

  function showMessage(message) {
    const element = getMessageElement();

    if (!element) {
      return;
    }

    element.textContent = message;
    element.hidden = false;
  }

  function hideMessage() {
    const element = getMessageElement();

    if (!element) {
      return;
    }

    element.hidden = true;
  }

  function lockCheckout(message = 'Checking cart requirements...') {
    setCheckoutAllowed(false);
    showMessage(message);
  }

  function parseGuardElement(element) {
    if (!element) {
      throw new Error('Cart guard JSON element was not found.');
    }

    return JSON.parse(element.textContent.trim());
  }

  function readCurrentGuardData() {
    return parseGuardElement(
      document.querySelector(SELECTORS.data)
    );
  }

  function getInvalidMessage(data) {
    const state = data?.state || {};
    const messages = data?.messages || {};
    const newQuantity = Number(state.newQuantity || 0);

    if (newQuantity > 0) {
      return (
        messages.newRestriction ||
        'NEW products must be purchased one at a time and by themselves unless the cart contains at least 10 straws or is over $750.'
      );
    }

    return (
      messages.minimum ||
      'Add enough qualifying products to reach 10 straws, or increase the cart total above $750.'
    );
  }

  function applyGuardData(data) {
    const valid = data?.state?.isValid === true;

    if (valid) {
      setCheckoutAllowed(true);
      hideMessage();
    } else {
      setCheckoutAllowed(false);
      showMessage(getInvalidMessage(data));
    }

    return valid;
  }

  async function getCartSignature() {
    const response = await fetch(`${ROOT}cart.js`, {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        Accept: 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(
        `Unable to read cart: ${response.status}`
      );
    }

    const cart = await response.json();

    return JSON.stringify({
      itemCount: cart.item_count,
      totalPrice: cart.total_price,
      items: cart.items.map((item) => ({
        key: item.key,
        quantity: item.quantity
      }))
    });
  }

  async function waitForCartToSettle() {
    /*
     * Give the theme time to begin its cart/change.js request.
     */
    const elapsed = Date.now() - lastCartAction;
    const minimumDelay = 700;

    if (elapsed < minimumDelay) {
      await sleep(minimumDelay - elapsed);
    }

    /*
     * Confirm that two consecutive cart reads match.
     */
    let previousSignature = null;

    for (let attempt = 0; attempt < 6; attempt += 1) {
      const signature = await getCartSignature();

      if (
        previousSignature !== null &&
        signature === previousSignature
      ) {
        return;
      }

      previousSignature = signature;
      await sleep(250);
    }
  }

  function getSectionId() {
    const footer = document.querySelector(SELECTORS.footer);
    const sectionId = footer?.dataset?.id;

    if (!sectionId) {
      throw new Error(
        'The main cart footer section ID was not found.'
      );
    }

    return sectionId;
  }

  async function fetchFreshGuardData() {
    const sectionId = getSectionId();

    const url = new URL(
      window.location.pathname,
      window.location.origin
    );

    url.searchParams.set('sections', sectionId);
    url.searchParams.set(
      '_cart_guard',
      String(Date.now())
    );

    const response = await fetch(url.toString(), {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        Accept: 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(
        `Unable to render cart footer: ${response.status}`
      );
    }

    const sections = await response.json();
    const footerHTML = sections[sectionId];

    if (!footerHTML) {
      throw new Error(
        'Shopify did not return the cart footer section.'
      );
    }

    const parsedDocument = new DOMParser().parseFromString(
      footerHTML,
      'text/html'
    );

    const guardElement = parsedDocument.querySelector(
      SELECTORS.data
    );

    return parseGuardElement(guardElement);
  }

  async function performFreshValidation() {
    if (validationPromise) {
      return validationPromise;
    }

    validationPromise = (async () => {
      lockCheckout();

      await waitForCartToSettle();

      const freshData = await fetchFreshGuardData();

      return applyGuardData(freshData);
    })();

    try {
      return await validationPromise;
    } finally {
      validationPromise = null;
    }
  }

  function scheduleFreshValidation() {
    if (validationTimer) {
      window.clearTimeout(validationTimer);
    }

    validationTimer = window.setTimeout(async () => {
      try {
        await performFreshValidation();
      } catch (error) {
        console.error(
          '[Cart Guard] Automatic validation failed.',
          error
        );

        /*
         * Fail closed. Checkout remains disabled.
         */
        lockCheckout(
          'Please refresh the cart to confirm checkout requirements.'
        );
      }
    }, 500);
  }

  function markCartChanged() {
    lastCartAction = Date.now();

    /*
     * Do not allow checkout while Shopify updates the cart.
     */
    lockCheckout();
    scheduleFreshValidation();
  }

  async function handleCheckoutAttempt(event) {
    event.preventDefault();
    event.stopPropagation();

    if (
      typeof event.stopImmediatePropagation === 'function'
    ) {
      event.stopImmediatePropagation();
    }

    lockCheckout();

    try {
      const valid = await performFreshValidation();

      if (valid) {
        sessionStorage.removeItem(
          'snogoPendingCheckout'
        );

        window.location.assign(`${ROOT}checkout`);
      }
    } catch (error) {
      console.error(
        '[Cart Guard] Checkout validation failed.',
        error
      );

      /*
       * Reload as the final fallback. Liquid will calculate the
       * current cart again during the full page render.
       */
      sessionStorage.setItem(
        'snogoPendingCheckout',
        '1'
      );

      window.location.reload();
    }
  }

  function isCheckoutTarget(target) {
    return (
      target instanceof Element &&
      Boolean(target.closest(SELECTORS.checkout))
    );
  }

  function isQuantityControl(target) {
    return (
      target instanceof Element &&
      Boolean(
        target.closest(
          `${SELECTORS.quantityInput}, ${SELECTORS.quantityControl}`
        )
      )
    );
  }

  function initialize() {
    /*
     * Apply the server-calculated state on initial page load.
     */
    try {
      const initialData = readCurrentGuardData();
      const initiallyValid = applyGuardData(initialData);

      /*
       * A previous checkout validation failed and forced a reload.
       * The Liquid state is now freshly rendered.
       */
      if (
        sessionStorage.getItem('snogoPendingCheckout') ===
        '1'
      ) {
        sessionStorage.removeItem(
          'snogoPendingCheckout'
        );

        if (initiallyValid) {
          window.location.assign(`${ROOT}checkout`);
          return;
        }
      }
    } catch (error) {
      console.error(
        '[Cart Guard] Initial validation failed.',
        error
      );

      lockCheckout(
        'Please refresh the cart to confirm checkout requirements.'
      );
    }

    /*
     * Quantity values typed manually.
     */
    document.addEventListener(
      'input',
      (event) => {
        if (
          event.target instanceof Element &&
          event.target.matches(SELECTORS.quantityInput)
        ) {
          markCartChanged();
        }
      },
      true
    );

    document.addEventListener(
      'change',
      (event) => {
        if (
          event.target instanceof Element &&
          event.target.matches(SELECTORS.quantityInput)
        ) {
          markCartChanged();
        }
      },
      true
    );

    /*
     * Plus, minus, remove, and checkout buttons.
     */
    document.addEventListener(
      'click',
      (event) => {
        if (isCheckoutTarget(event.target)) {
          handleCheckoutAttempt(event);
          return;
        }

        if (isQuantityControl(event.target)) {
          markCartChanged();
        }
      },
      true
    );

    /*
     * Also protect keyboard or programmatic form submission.
     */
    document.addEventListener(
      'submit',
      (event) => {
        const submitter = event.submitter;

        if (
          submitter instanceof Element &&
          submitter.matches(SELECTORS.checkout)
        ) {
          handleCheckoutAttempt(event);
        }
      },
      true
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialize,
      { once: true }
    );
  } else {
    initialize();
  }
})();